// src/components/adminpanel/StructureTab.tsx
import { useState, useEffect, useCallback, memo } from "react";
import { useOrganizationsStore } from "@/store/organizationsStore";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash2, Save, Crown, Users, Eye, X, UserPlus, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import type { OrgNode } from "@/types/organization";

const TreeNode = memo(({
  node,
  level = 0,
  updateNode,
  addChild,
  deleteNode,
  expandedNodes,
  toggleExpand
}: {
  node: OrgNode;
  level: number;
  updateNode: (id: string, updates: Partial<OrgNode>) => void;
  addChild: (parentId: string) => void;
  deleteNode: (id: string) => void;
  expandedNodes: Set<string>;
  toggleExpand: (id: string) => void;
}) => {
  const isExpanded = expandedNodes.has(node.id);

  return (
    <div className="mb-4">
      <div className={`flex items-center gap-4 rounded-3xl bg-slate-900/90 border border-purple-700/50 p-6 hover:border-purple-500 transition-all duration-300 ${level > 0 ? "ml-10" : ""}`}>
        <button 
          onClick={() => toggleExpand(node.id)} 
          className="w-10 h-10 flex items-center justify-center text-purple-400 hover:text-white transition-colors"
        >
          {node.children && node.children.length > 0 ? (
            isExpanded ? <ChevronDown className="h-6 w-6" /> : <ChevronRight className="h-6 w-6" />
          ) : <div className="w-6" />}
        </button>

        <div className="text-5xl flex-shrink-0">{getIconEmoji(node.icon)}</div>

        <div className="flex-1 grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Input
            value={node.name}
            onChange={(e) => updateNode(node.id, { name: e.target.value })}
            className="bg-slate-800 border-purple-700 text-white font-semibold text-lg h-14 rounded-2xl"
            placeholder="Название должности"
          />
          <Input
            value={node.personName || ""}
            onChange={(e) => updateNode(node.id, { personName: e.target.value })}
            className="bg-slate-800 border-purple-700 text-purple-100 h-14 rounded-2xl"
            placeholder="ФИО сотрудника"
          />
          <Input
            value={node.role || ""}
            onChange={(e) => updateNode(node.id, { role: e.target.value })}
            className="bg-slate-800 border-purple-700 text-purple-100 h-14 rounded-2xl"
            placeholder="Роль / описание"
          />
        </div>

        <div className="flex gap-3">
          <Button onClick={() => addChild(node.id)} variant="outline" className="border-purple-500 hover:bg-purple-900/50 h-14 w-14 rounded-2xl">
            <Plus className="h-5 w-5" />
          </Button>
          <Button onClick={() => deleteNode(node.id)} variant="destructive" className="h-14 w-14 rounded-2xl">
            <Trash2 className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {node.children && node.children.length > 0 && isExpanded && (
        <div className="mt-4 pl-12 border-l-2 border-purple-700/30">
          {node.children.map((child) => (
            <TreeNode 
              key={child.id} 
              node={child} 
              level={level + 1} 
              updateNode={updateNode} 
              addChild={addChild} 
              deleteNode={deleteNode} 
              expandedNodes={expandedNodes} 
              toggleExpand={toggleExpand} 
            />
          ))}
        </div>
      )}
    </div>
  );
});

TreeNode.displayName = "TreeNode";

export default function StructureTab() {
  const { organizations, updateStructure, createDefaultUsers } = useOrganizationsStore();

  const [selectedOrgId, setSelectedOrgId] = useState<string>("");
  const [structure, setStructure] = useState<OrgNode[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [isCreatingUsers, setIsCreatingUsers] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (organizations?.length > 0 && !selectedOrgId) {
      const first = organizations[0];
      setSelectedOrgId(first.id);
      setStructure(first.structure || []);
    }
  }, [organizations]);

  useEffect(() => {
    if (selectedOrgId && organizations) {
      const org = organizations.find((o) => o.id === selectedOrgId);
      if (org) setStructure(org.structure || []);
    }
  }, [selectedOrgId, organizations]);

  const selectedOrg = organizations?.find((o) => o.id === selectedOrgId);

  const saveStructure = async (silent = false) => {
    if (!selectedOrgId) return;
    await updateStructure(selectedOrgId, structure);
    setIsEditing(false);
    if (!silent) toast({ title: "✅ Структура сохранена" });
  };

  const collectUsersFromStructure = (nodes: OrgNode[]) => {
    const users: any[] = [];
    const traverse = (nodeList: OrgNode[]) => {
      nodeList.forEach((node) => {
        if (node.personName?.trim()) {
          users.push({
            numericId: node.numericId || users.length + 1,
            login: node.personName.split(" ")[0] || `user${users.length + 1}`,
            name: node.personName.trim(),
            role: node.role || "Сотрудник",
            canCreateTask: true,
            isAdmin: false,
          });
        }
        if (node.children?.length) traverse(node.children);
      });
    };
    traverse(nodes);
    return users;
  };

  const handleCreateUsers = async () => {
    if (!selectedOrgId) {
      toast({ title: "Выберите организацию", variant: "destructive" });
      return;
    }

    const usersToCreate = collectUsersFromStructure(structure);
    if (usersToCreate.length === 0) {
      toast({ title: "Нет пользователей", description: "Заполните поле ФИО хотя бы у одного сотрудника", variant: "destructive" });
      return;
    }

    setIsCreatingUsers(true);
    const success = await createDefaultUsers(selectedOrgId, usersToCreate);
    setIsCreatingUsers(false);

    if (success) {
      await saveStructure(true);
      toast({ title: `✅ Создано ${usersToCreate.length} пользователей` });
    }
  };

  const updateNode = useCallback((id: string, updates: Partial<OrgNode>) => {
    setStructure((prev) => {
      const fn = (nodes: OrgNode[]): OrgNode[] =>
        nodes.map((node) =>
          node.id === id ? { ...node, ...updates } : node.children ? { ...node, children: fn(node.children) } : node
        );
      return fn(prev);
    });
    setIsEditing(true);
  }, []);

  const addChild = useCallback((parentId: string) => {
    const newChild: OrgNode = {
      id: `node-${Date.now()}`,
      name: "Новая должность",
      personName: "",
      role: "Сотрудник",
      icon: "Users",
      order: 1,
      children: [],
    };

    setStructure((prev) => {
      const fn = (nodes: OrgNode[]): OrgNode[] =>
        nodes.map((node) =>
          node.id === parentId
            ? { ...node, children: [...(node.children || []), newChild] }
            : node.children ? { ...node, children: fn(node.children) } : node
        );
      return fn(prev);
    });
    setIsEditing(true);
    setExpandedNodes((prev) => new Set(prev).add(parentId));
  }, []);

  const deleteNode = useCallback((id: string) => {
    if (!confirm("Удалить этот узел вместе с подчинёнными?")) return;
    setStructure((prev) => {
      const fn = (nodes: OrgNode[]): OrgNode[] =>
        nodes
          .filter((n) => n.id !== id)
          .map((n) => ({ ...n, children: n.children ? fn(n.children) : [] }));
      return fn(prev);
    });
    setIsEditing(true);
  }, []);

  const toggleExpand = useCallback((id: string) => {
    setExpandedNodes((prev) => {
      const newSet = new Set(prev);
      newSet.has(id) ? newSet.delete(id) : newSet.add(id);
      return newSet;
    });
  }, []);

  const getIconEmoji = (icon?: string) => {
    const map: Record<string, string> = {
      Crown: "👑", Bomb: "💣", Users: "👥", Sword: "⚔️", Zap: "⚡", Shield: "🛡️", Award: "🏅"
    };
    return map[icon || ""] || "👤";
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-indigo-950 p-6 md:p-10">
      <div className="max-w-7xl mx-auto">

        {/* Заголовок */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-4 bg-white/10 backdrop-blur-2xl border border-white/20 rounded-3xl px-10 py-5 mb-6">
            <Crown className="h-12 w-12 text-purple-400" />
            <h1 className="text-5xl font-bold text-white tracking-tighter">Структура организаций</h1>
          </div>
          <p className="text-purple-300 text-2xl">Гибкий редактор иерархии должностей</p>
        </div>

        {/* Панель управления */}
        <div className="flex flex-col lg:flex-row gap-6 mb-10 items-end">
          <select
            value={selectedOrgId}
            onChange={(e) => setSelectedOrgId(e.target.value)}
            className="flex-1 bg-slate-900 border border-purple-700/50 text-white text-xl rounded-3xl px-8 py-6 focus:border-purple-500"
          >
            {organizations?.map((org) => (
              <option key={org.id} value={org.id}>{org.shortName}</option>
            ))}
          </select>

          <div className="flex flex-wrap gap-4">
            <Button 
              onClick={() => saveStructure()} 
              disabled={!isEditing}
              className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 px-10 py-6 rounded-3xl text-lg flex items-center gap-3 shadow-xl shadow-emerald-600/30"
            >
              <Save className="h-6 w-6" /> Сохранить структуру
            </Button>

            {selectedOrgId && (
              <Button 
                onClick={handleCreateUsers} 
                disabled={isCreatingUsers}
                className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 px-10 py-6 rounded-3xl text-lg flex items-center gap-3"
              >
                <UserPlus className="h-6 w-6" />
                {isCreatingUsers ? "Создаём..." : "Создать пользователей"}
              </Button>
            )}

            <Button 
              onClick={() => setShowPreview(true)} 
              variant="outline" 
              className="border-purple-500 hover:bg-purple-900/50 px-10 py-6 rounded-3xl text-lg flex items-center gap-3"
            >
              <Eye className="h-6 w-6" /> Предпросмотр
            </Button>
          </div>
        </div>

        {/* Основной редактор */}
        {selectedOrg ? (
          <div className="bg-gradient-to-br from-slate-900 to-purple-950/70 border border-purple-700/50 rounded-3xl p-8 lg:p-12 shadow-2xl">
            {structure.length === 0 ? (
              <div className="text-center py-32">
                <Users className="mx-auto h-28 w-28 text-purple-400 mb-8 opacity-70" />
                <h3 className="text-4xl font-bold text-white mb-4">Структура пока пустая</h3>
                <Button 
                  onClick={() => {
                    setStructure([{
                      id: `root-${Date.now()}`,
                      name: "Руководитель",
                      personName: "",
                      role: "Руководитель",
                      icon: "Crown",
                      order: 1,
                      children: []
                    }]);
                    setIsEditing(true);
                  }} 
                  className="mt-6 px-10 py-6 text-lg bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                >
                  <Plus className="mr-3 h-6 w-6" /> Создать первый уровень
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {structure.map((node) => (
                  <TreeNode 
                    key={node.id} 
                    node={node} 
                    level={0} 
                    updateNode={updateNode} 
                    addChild={addChild} 
                    deleteNode={deleteNode} 
                    expandedNodes={expandedNodes} 
                    toggleExpand={toggleExpand} 
                  />
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-32 text-purple-300 text-2xl bg-slate-900 border border-purple-700/50 rounded-3xl">
            Выберите организацию выше
          </div>
        )}
      </div>

      {/* Предпросмотр */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/90 z-[200] flex items-center justify-center p-6">
          <div className="bg-slate-950 border border-purple-600 rounded-3xl w-full max-w-md overflow-hidden shadow-2xl">
            <div className="p-6 border-b border-purple-700 flex items-center justify-between bg-slate-900">
              <div className="flex items-center gap-3">
                <Crown className="h-8 w-8 text-purple-400" />
                <h2 className="text-2xl font-bold text-white">Предпросмотр в сайдбаре</h2>
              </div>
              <Button onClick={() => setShowPreview(false)} variant="ghost" size="icon" className="text-purple-400 hover:text-white">
                <X className="h-6 w-6" />
              </Button>
            </div>

            <div className="p-8 space-y-8 max-h-[70vh] overflow-auto">
              {structure.length === 0 ? (
                <p className="text-center text-purple-400 py-12">Структура пуста</p>
              ) : (
                structure.map((dept) => (
                  <div key={dept.id} className="space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="text-5xl mt-1">{getIconEmoji(dept.icon)}</div>
                      <div className="flex-1">
                        <div className="font-semibold text-white text-lg">{dept.name}</div>
                        {dept.personName && <div className="text-purple-300 text-sm mt-1">{dept.personName}</div>}
                      </div>
                    </div>

                    {dept.children?.map((pos) => (
                      <div key={pos.id} className="pl-12 flex items-start gap-4">
                        <div className="text-4xl mt-1">{getIconEmoji(pos.icon)}</div>
                        <div className="flex-1">
                          <div className="font-medium text-white">{pos.name}</div>
                          {pos.personName && <div className="text-purple-400 text-sm">{pos.personName}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const getIconEmoji = (icon?: string) => {
  const map: Record<string, string> = {
    Crown: "👑", Bomb: "💣", Users: "👥", Sword: "⚔️", Zap: "⚡", Shield: "🛡️", Award: "🏅"
  };
  return map[icon || ""] || "👤";
};